import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useOrganization } from '../../context/OrgContext';
import GenericModuleFlow from './workflows/WorkflowInterface';
import LoadingPage from '../Loading';

const WorkflowEntry = () => {
  const { workflowSlug } = useParams();
  const navigate = useNavigate();
  const { institution } = useOrganization();

  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build workflow path based on URL structure
  const buildWorkflowPath = () => {
    if (!institution?.id || !workflowSlug) return null;

    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    
    // Check if it's a root-level workflow (/workflows/slug)
    if (pathSegments.length === 2 && pathSegments[0] === 'workflows') {
      return `colleges/${institution.id}/workflows`;
    }
    
    // It's a nested workflow (/section/interface/workflows/slug)
    // Remove 'workflows' and the slug to get the path to the interface
    const pathToInterface = pathSegments.slice(0, -2); // Remove 'workflows' and slug
    
    let collectionPath = `colleges/${institution.id}`;
    
    // Build path to the interface containing the workflow
    for (let i = 0; i < pathToInterface.length; i += 2) {
      // Add section collection
      collectionPath += `/${pathToInterface[i]}.section`;
      
      // If there's an interface after this section, add it as a document
      if (i + 1 < pathToInterface.length) {
        collectionPath += `/${pathToInterface[i + 1]}`;
      }
    }
    
    return `${collectionPath}/workflows`;
  };

  // Fetch workflow data
  useEffect(() => {
    const fetchWorkflow = async () => {
      if (!institution?.id || !workflowSlug) return;

      try {
        setLoading(true);
        setError(null);

        const workflowCollectionPath = buildWorkflowPath();
        if (!workflowCollectionPath) {
          setError('Invalid workflow path');
          return;
        }

        const workflowRef = doc(db, workflowCollectionPath, workflowSlug);
        const workflowDoc = await getDoc(workflowRef);

        if (workflowDoc.exists()) {
          setWorkflow({
            id: workflowDoc.id,
            ...workflowDoc.data()
          });
        } else {
          setError('Workflow not found');
        }
      } catch (err) {
        console.error('Error fetching workflow:', err);
        setError('Failed to load workflow');
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflow();
  }, [institution?.id, workflowSlug]);

  // Handle workflow completion
  const handleWorkflowComplete = (nodes, edges) => {
    console.log(`Workflow "${workflow?.name}" completed!`, { nodes, edges });

    // You can add workflow-specific completion logic here
    // For example:
    // - Save completion status to Firestore
    // - Trigger notifications
    // - Update workflow status
    // - Navigate to a success page

    // Example: Navigate back to dashboard after completion
    navigate('/', {
      state: {
        message: `Workflow "${workflow?.name}" completed successfully!`
      }
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="w-full h-screen flex justify-center items-center bg-gradient-to-br from-[#0a0a0a] via-[#0f0b08] to-[#0a0a0a]">
        <LoadingPage />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full h-screen flex flex-col justify-center items-center bg-gradient-to-br from-[#0a0a0a] via-[#0f0b08] to-[#0a0a0a] text-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Workflow Not Found</h1>
          <p className="text-xl text-gray-400 mb-8">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Main workflow interface
  return (
    <div className="w-full h-screen">
      {workflow && (
        <GenericModuleFlow
          moduleName={workflow.slug || workflow.name?.toLowerCase()}
          workflowData={workflow}
          onModuleComplete={handleWorkflowComplete}
          workflowTitle={workflow.name}
          workflowDescription={workflow.description}
        />
      )}
    </div>
  );
};

export default WorkflowEntry;